import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaCog, FaComments, FaHistory, FaRoute, FaStore, FaUserCircle, FaWifi } from 'react-icons/fa'
import { Avatar, Banner, FoBtn, Pill, Seg, Sheet } from './fieldUi'
import { FieldContext, useWorkerPosition } from './fieldContext'
import { dayOf, isRealToday, pendingCount, unreadFor, workDate, orderIndex, visitIndex } from '../store'
import { driverStops, repStops, summarizeStops } from './stops'
import { clockLabel } from '../geo'
import RouteScreen from './RouteScreen'
import StopScreen from './StopScreen'
import DeliveryScreen from './DeliveryScreen'
import OrderBuilder from './OrderBuilder'
import ReturnBuilder from './ReturnBuilder'
import StartDayScreen from './StartDayScreen'
import EndDayScreen from './EndDayScreen'
import StoresScreen from './StoresScreen'
import DayDetailScreen from './DayDetailScreen'
import HistoryScreen from './HistoryScreen'
import ChatScreen from './ChatScreen'
import MeScreen from './MeScreen'
import '../../../css/field-ops-app.css'

/**
 * Field app (2026-11-04): what a salesperson or a driver sees on a phone or tablet. The active tab comes from the URL
 * (props tab / onTab) so a reload or a shared link opens the same tab. One shell for both roles:
 * Route (list + map), Stores (reps), History, Chat and Me, plus full-screen pages pushed on top (stop, order, return,
 * start / end of day). All data lives in the shared demo store; nothing is saved when the page reloads.
 */
const TABS_REP = [
    { key: 'route', label: 'Route', icon: <FaRoute /> }, { key: 'stores', label: 'Stores', icon: <FaStore /> },
    { key: 'history', label: 'History', icon: <FaHistory /> }, { key: 'chat', label: 'Chat', icon: <FaComments /> }, { key: 'me', label: 'Me', icon: <FaUserCircle /> },
]
const TABS_DRIVER = [
    { key: 'route', label: 'Route', icon: <FaRoute /> }, { key: 'history', label: 'History', icon: <FaHistory /> },
    { key: 'chat', label: 'Chat', icon: <FaComments /> }, { key: 'me', label: 'Me', icon: <FaUserCircle /> },
]

const nowMinutes = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes() }

export default function FieldApp({ ctx, actor, tab, onTab }) {
    const { state, dispatch } = ctx
    const field = state.field
    const setTab = onTab
    const [stack, setStack] = useState([])
    const [date, setDate] = useState(workDate())
    const [demoOpen, setDemoOpen] = useState(false)
    const [selected, setSelected] = useState(null) // stop picked on the map / list highlight
    const rootRef = useRef(null)
    const tabs = actor.kind === 'rep' ? TABS_REP : TABS_DRIVER

    // a different demo user starts from a clean navigation state
    useEffect(() => { setStack([]); setSelected(null); setDate(workDate()) }, [actor.key])

    const vIdx = useMemo(() => visitIndex(state.sales), [state.sales])
    const oIdx = useMemo(() => orderIndex(state.sales), [state.sales])

    // first stop of the day (used to place the simulated "start of day" position)
    const firstStop = useMemo(() => {
        const list = actor.kind === 'rep' ? repStops(state, actor, date, null, 480, vIdx, oIdx) : driverStops(state, actor, date, null, 480)
        return list[0] || null
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actor.key, date])
    const gps = useWorkerPosition(field, actor.key, firstStop)

    const day = dayOf(field, actor.key, date)
    const isToday = date === workDate()
    const startMin = (() => { const n = nowMinutes(); return isToday && n >= 360 && n <= 1080 ? n : 480 })()
    const stops = useMemo(() => (
        actor.kind === 'rep' ? repStops(state, actor, date, gps.pos, startMin, vIdx, oIdx) : driverStops(state, actor, date, gps.pos, startMin)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [state.sales, state.drivers, actor.key, date, gps.pos, startMin])
    const summary = useMemo(() => summarizeStops(stops, actor.kind, gps.pos), [stops, actor.kind, gps.pos])

    const nav = {
        push: (screen) => setStack((s) => s.concat([screen])),
        pop: () => setStack((s) => s.slice(0, -1)),
        replace: (screen) => setStack((s) => s.slice(0, -1).concat([screen])),
        reset: () => setStack([]),
        go: (t) => { setStack([]); setTab(t) },
    }
    const online = field.online
    const send = (action) => dispatch(Object.assign({ pending: !online }, action))
    const value = { ctx: ctx, state: state, dispatch: dispatch, send: send, actor: actor, kind: actor.kind, date: date, setDate: setDate, isToday: isToday, isRealToday: isRealToday(date), day: day, nav: nav, gps: gps, online: online, toast: ctx.toast, stops: stops, summary: summary, selected: selected, setSelected: setSelected, vIdx: vIdx, oIdx: oIdx, startMin: startMin, tab: tab }

    const chatUnread = unreadFor(field, actor.key, 'worker')
    const pending = pendingCount(state)
    const top = stack.length ? stack[stack.length - 1] : null

    const renderScreen = (s) => {
        switch (s.type) {
            case 'stop': return s.kind === 'delivery' ? <DeliveryScreen key={'d' + s.id} id={s.id} autoCheckin={s.checkin} /> : <StopScreen key={'v' + s.id} id={s.id} autoCheckin={s.checkin} />
            case 'order': return <OrderBuilder key={'o' + s.visitId} visitId={s.visitId} />
            case 'return': return <ReturnBuilder key={'r' + s.refId} source={s.source} refId={s.refId} />
            case 'startday': return <StartDayScreen key="sd" />
            case 'endday': return <EndDayScreen key="ed" />
            case 'store': return <StopScreen key={'s' + s.storeId} storeId={s.storeId} />
            case 'visit': return <StopScreen key={'hv' + s.id} id={s.id} readOnly />
            case 'delivery': return <DeliveryScreen key={'hd' + s.id} id={s.id} readOnly />
            case 'day': return <DayDetailScreen key={'day' + s.date} date={s.date} />
            default: return null
        }
    }

    return (
        <FieldContext.Provider value={value}>
            <div className="fo-app" ref={rootRef}>
                <div className="fo-appbar">
                    <div className="fo-appbar__who">
                        <Avatar name={actor.name} tone={actor.kind === 'rep' ? 'blue' : 'green'} />
                        <div><strong>{actor.name}</strong><span>{actor.kind === 'rep' ? 'Salesperson · Altitude' : 'Driver · ' + driverVehicle(state, actor)}</span></div>
                    </div>
                    <div className="fo-appbar__right">
                        {!online ? <Pill tone="amber" icon={<FaWifi />}>Offline{pending ? ' · ' + pending + ' to send' : ''}</Pill> : (pending ? <Pill tone="blue">Sending {pending}…</Pill> : <Pill tone="green" icon={<FaWifi />}>Online</Pill>)}
                        <button type="button" className="fo-appbar__cog" onClick={() => setDemoOpen(true)} aria-label="Demo controls" title="Demo controls"><FaCog /></button>
                    </div>
                </div>

                {!online ? <Banner tone="warn" title="You are offline.">Check-ins, orders, returns and messages are saved on this device and sent when the signal is back.</Banner> : null}
                {gps.source === 'denied' || (gps.source === 'real' && gps.error) ? <Banner tone="warn" title="Location unavailable.">{gps.error || 'Turn on location to check in automatically.'} You can still check in manually; the office will see it as a manual check-in.</Banner> : null}

                <div className="fo-tabs-top">
                    <Seg full items={tabs.map((t) => ({ key: t.key, label: t.label, icon: t.icon, badge: t.key === 'chat' && chatUnread ? chatUnread : null }))} value={tab} onChange={(k) => { setTab(k); setStack([]) }} />
                </div>

                <div className="fo-main">
                    {tab === 'route' ? <RouteScreen /> : null}
                    {tab === 'stores' ? <StoresScreen /> : null}
                    {tab === 'history' ? <HistoryScreen /> : null}
                    {tab === 'chat' ? <ChatScreen /> : null}
                    {tab === 'me' ? <MeScreen /> : null}
                </div>

                <nav className="fo-tabbar" aria-label="Field app">
                    {tabs.map((t) => (
                        <button key={t.key} type="button" className={'fo-tabbar__btn' + (tab === t.key ? ' is-on' : '')} onClick={() => { setTab(t.key); setStack([]) }}>
                            <span className="fo-tabbar__icon">{t.icon}{t.key === 'chat' && chatUnread ? <i className="fo-tabbar__dot">{chatUnread}</i> : null}</span>
                            <span>{t.label}</span>
                        </button>
                    ))}
                </nav>

                {top ? renderScreen(top) : null}

                <DemoControls open={demoOpen} onClose={() => setDemoOpen(false)} />
            </div>
        </FieldContext.Provider>
    )
}

const driverVehicle = (state, actor) => {
    const d = state.drivers.drivers.filter((x) => x.id === actor.driverId)[0]
    const v = d ? state.drivers.vehicles.filter((x) => x.id === d.vehicleId)[0] : null
    return v ? v.name : 'no vehicle'
}

/** Demo-only switches: network, GPS and where the simulated phone is. Not part of the real app. */
const DemoControls = ({ open, onClose }) => {
    const f = React.useContext(FieldContext)
    if (!f) return null
    const field = f.state.field
    return (
        <Sheet show={open} onClose={onClose} title="Demo controls" subtitle="Only in this preview — to test situations that happen in the field"
            footer={<FoBtn kind="primary" onClick={onClose}>Done</FoBtn>}>
            <div className="fo-demo">
                <div className="fo-demo__row">
                    <div><strong>Network</strong><span>Offline saves everything on the device and sends it when you go back online.</span></div>
                    <Seg items={[{ key: 'on', label: 'Online' }, { key: 'off', label: 'Offline' }]} value={field.online ? 'on' : 'off'} onChange={(k) => f.dispatch({ type: 'NET_SET', online: k === 'on' })} />
                </div>
                <div className="fo-demo__row">
                    <div><strong>Location (GPS)</strong><span>Simulated follows the stops. Real asks this browser for your location.</span></div>
                    <Seg items={[{ key: 'sim', label: 'Simulated' }, { key: 'real', label: 'Real' }, { key: 'denied', label: 'Off' }]} value={field.gps} onChange={(k) => f.dispatch({ type: 'DEMO_SET', patch: { gps: k } })} />
                </div>
                <div className="fo-demo__row">
                    <div><strong>Where is the phone at check-in?</strong><span>“Away” puts it about 1.6 miles from the store to test the far-from-store warning.</span></div>
                    <Seg items={[{ key: 'at', label: 'At the store' }, { key: 'away', label: 'Away' }]} value={field.away ? 'away' : 'at'} onChange={(k) => f.dispatch({ type: 'DEMO_SET', patch: { away: k === 'away' } })} />
                </div>
                <div className="fo-demo__note">Start time used for the estimates: {clockLabel(f.startMin)}.</div>
            </div>
        </Sheet>
    )
}
